import {html, css, LitElement, nothing} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';

import {gravatarStore} from '@sdb-develop/learning-useritem/tcg-gravatar-store.js';
import {userStore} from '@/shared/stores/userStore';
import {settingsStore} from '@/shared/stores/settingsStore';
import {debounce} from '@/shared/utils/debounce';
import {signInToHl} from '@/shared/utils/hl-signin.js';
import {installScrollRestoration} from '@/shared/utils/scrollRestoration';

// NOTE: this polyfill is needed when the URLPattern doesn't exist (i.e. Firefox)
// if (!globalThis.URLPattern) {
// await import('urlpattern-polyfill');
// }
// SR: below works for iOS14 and lower, the above way of import does not.
import 'urlpattern-polyfill';
import '@/auth/aca-signin-page.js';
import '@polymer/iron-location/iron-location.js';
import '@sdb-develop/learning-signin/tcg-signin-aware.js';

import '@/shared/components/authenticate-callback.js';
import '@/shared/noresults/tcg-noresults.js';

import {store} from '@/store';
import {connect} from 'pwa-helpers/connect-mixin.js';
import {Router} from '@lit-labs/router';
import 'wc-context-redux/store-context';

//import '@/auth/aca-silent-signin-page';

//Workaround for:
//WARNING: <template> inside <vaadin-dialog> is no longer supported.
import '@vaadin/polymer-legacy-adapter/template-renderer.js';

import {VaadinStyleLoader} from '@sdb-develop/learning-frontend/dist/src/vaadin-style-loader.js';
import {colors} from '@sdb-develop/learning-frontend/dist/src/sdb-colors.js';
import {isRouteAllowed, isAwaitingMenu} from '@/shared/utils/routeAccess.js';

import('@microsoft/applicationinsights-web').then(
    (module) => {
        const appInsights = new module.ApplicationInsights({
            config: {
                instrumentationKey: '76607fcc-a4ec-4d34-8233-572fd78923cf',
                enableAutoRouteTracking: true,
                enableUnhandledPromiseRejectionTracking: true,
                enableAjaxErrorStatusText: true,
                // Aan, maar de telemetry-initializer hieronder gooit alles weg
                // wat gelukt is. Zonder dat stonden deze twee op `true` en was
                // de dependencies-tabel leeg: 3 rijen in 8 uur. Bij een storing
                // was daarmee niet te zien welke backend faalde, en dat kostte
                // op 10 september een dag zoekwerk. Alleen de mislukte aanroepen
                // bewaren geeft dat inzicht wel, zonder het volume van elke
                // geslaagde call.
                disableAjaxTracking: false,
                disableFetchTracking: false,
                // Chrome is phasing out `unload` and blocks it by permissions policy, which logs a
                // violation on every page. The SDK still flushes on pagehide, visibilitychange and
                // beforeunload, so no telemetry is lost.
                disablePageUnloadEvents: ['unload'],
                /* ...Other Configuration Options... */
            },
        });

        // Track client resolution using trackMetric
        function trackClientResolutionMetrics() {
            // Screen dimensions
            appInsights.trackMetric({
                name: 'ScreenWidth',
                average: window.screen.width,
            });

            appInsights.trackMetric({
                name: 'ScreenHeight',
                average: window.screen.height,
            });

            appInsights.trackMetric({
                name: 'PixelRatio',
                average: window.devicePixelRatio || 1,
            });

            // Viewport dimensions
            appInsights.trackMetric({
                name: 'ViewportWidth',
                average: window.innerWidth,
            });

            appInsights.trackMetric({
                name: 'ViewportHeight',
                average: window.innerHeight,
            });

            // You can add properties to provide context for your metrics
            const properties = {
                deviceCategory: determineDeviceCategory(window.innerWidth),
                orientation: window.screen.orientation ? window.screen.orientation.type : 'unknown',
                userAgent: navigator.userAgent,
            };

            // You can also track aspect ratio as a derived metric
            const aspectRatio = window.screen.width / window.screen.height;

            appInsights.trackMetric({
                name: 'AspectRatio',
                average: aspectRatio,
                properties,
            });
        }

        // Helper function to categorize devices
        function determineDeviceCategory(width) {
            if (width < 576) return 'mobile';
            if (width < 992) return 'tablet';
            if (width < 1200) return 'laptop';
            return 'desktop';
        }

        // Track on page load
        window.addEventListener('load', trackClientResolutionMetrics);

        // Track on resize with debounce
        let resizeTimeout = null;

        window.addEventListener('resize', () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = window.setTimeout(trackClientResolutionMetrics, 300);
        });

        window.appInsights = window.appInsights || appInsights;

        // Add telemetry initializer to filter out specific URLs
        appInsights.addTelemetryInitializer((envelope) => {
            if (envelope.baseType === 'RemoteDependencyData') {
                const dependency = envelope.baseData;

                // Alleen mislukte aanroepen bewaren. Een geslaagde call zegt
                // bij een storing niets en zou het volume vermenigvuldigen met
                // elke API-aanroep van elke gebruiker.
                if (dependency.success === true) {
                    return false;
                }

                // Exclude specific URL from being tracked
                if (dependency.target) {
                    if (dependency.target.indexOf('https://js.monitor.azure.com/') !== -1) {
                        return false; // Stop this telemetry from being sent
                    } else if (
                        dependency.target.indexOf('https://cdn.sdbleerplatform.nl/') !== -1
                    ) {
                        return false; // Stop this telemetry from being sent
                    } else if (
                        dependency.target.indexOf('https://studio-playerapi.competence.biz/') !== -1
                    ) {
                        return false; // Stop this telemetry from being sent
                    } else if (
                        dependency.target.indexOf('https://collector.exceptionless.io/') !== -1
                    ) {
                        return false; // Stop this telemetry from being sent
                    }
                }
            }

            return true; // Allow other telemetry to pass through
        });

        appInsights.loadAppInsights();
    },
    (err) => {
        console.error(err);
    },
);

@customElement('aca-app')
class AcaApp extends connect(store)(LitElement) {
    constructor() {
        super();

        this.store = store;

        this._loadedModules = {};

        this._silentSignIn = true;
        this._signIn = false;
        this.signedIn = false;
        this._hasHeaders = false;

        this._router = new Router(this, [
            // {path: '/portaldoesnotexist', render: () => html`<aca-portal-does-not-exists-page></aca-portal-does-not-exists-page>`},
            // {path: '/unauthorized', render: () => html`<aca-unauthorized-page></aca-unauthorized-page>`},

            {
                path: '/callback',
                render: () => html`<authenticate-callback></authenticate-callback>`,
                enter: () => {
                    this._signIn = false;
                },
            },
            {
                path: '/elearning-close-dialog',
                render: () => html`<sdb-elearning-close-dialog></sdb-elearning-close-dialog>`,
                enter: async () => {
                    await import(
                        '@sdb-develop/learning-composition-player/components/elearning/sdb-elearning-close-dialog.js'
                    );
                },
            },
            {
                path: '/close-preview',
                render: () => html`<sdb-preview-close-dialog></sdb-preview-close-dialog>`,
                enter: async () => {
                    await import(
                        '@sdb-develop/learning-composition-editor/components/preview/sdb-preview-close-dialog.js'
                    );
                },
            },
            {
                path: '/learningplan-close-dialog',
                render: () => html`<tcg-learningplan-dialog-close></tcg-learningplan-dialog-close>`,
                enter: async () => {
                    await import('@/learningplan/tcg-learningplan-dialog-close.js');
                },
            },
            {
                path: '/go-to-uservoice',
                render: () => html`<sdb-uservoice></sdb-uservoice>`,
                enter: async () => {
                    await import('@/shared/components/sdb-uservoice.js');
                },
            },
            {
                path: '/scorm-player-close-dialog',
                render: () => html`<tcg-scorm-player-dialog-close></tcg-scorm-player-dialog-close>`,
                enter: async () => {
                    await import('@/scorm/tcg-scorm-player-dialog-close.js');
                },
            },
            {
                path: '/costregistration-close-dialog',
                render: () =>
                    html`<tcg-costregistration-dialog-close></tcg-costregistration-dialog-close>`,
                enter: async () => {
                    await import('@/costregistration/tcg-costregistration-dialog-close.js');
                },
            },
            {
                path: '/*',
                render: (params) => this._renderAcaMainPage(params),
                enter: async () => {
                    await import('@/app/aca-main-page.js');
                    await import(
                        '@sdb-develop/learning-composition-editor/components/preview/sdb-embedded-preview-dialog.js'
                    );
                    await import(
                        '@sdb-develop/learning-composition-player/components/elearning/sdb-embedded-elearning-dialog.js'
                    );
                    import('@/costregistration/tcg-costregistration-dialog.js');
                    import('@/learningplan/tcg-learningplan-dialog.js');
                    import('@/scorm/tcg-scorm-player-dialog.js');
                },
            },
        ]);

        this.addEventListener('route-to', this._routeTo);
        this.addEventListener('route-to-planning-schedule-list', this._routeToPlanningScheduleList);
        this.addEventListener('launch-elearning-item', this._launchElearningItem);

        // After "back", return to the same position on the page.
        installScrollRestoration();

        // Make sure all custom styles are loaded before showing vaadin elements.
        // After showing styles cannot be registered.
        VaadinStyleLoader.registerAllStyles();
    }

    _isRouteAllowed(menu, route) {
        return isRouteAllowed(menu, route);
    }

    async _loadModule(path, importFn) {
        if (this._loadedModules[path]) return;

        await importFn();

        this._loadedModules[path] = true;
    }

    _routeTo(e) {
        const url = this._router.link(e.detail.route);
        const state = {};

        if (e.detail.prevRoute) {
            // if prevRoute is a (true) boolean use window.location.pathname otherwise use the value of prevRoute
            state.prevRoute =
                e.detail.prevRoute === true ? window.location.pathname : e.detail.prevRoute;
        } else if (e.detail.skillId) {
            // skill details navigate with a skillId and expect back to return here
            state.prevRoute = window.location.pathname;
        }
        this.__routeTo(url, state, e.detail.skipHistory);
    }

    _routeToPlanningScheduleList(e) {
        const url = `/training/planning/${e.detail}/schedules/list`;
        const prevRoute = window.location.pathname;

        this.__routeTo(url, {prevRoute});
    }

    __routeTo(url, state = {}, skipHistory = false) {
        if (!skipHistory) {
            window.history.pushState(state, null, url);
        }
        this._router.goto(url);
        window.dispatchEvent(new CustomEvent('location-changed'));
    }

    _launchElearningItem(e) {
        this.elearningDialog.open(e.detail.item);
    }

    static get styles() {
        return [
            colors,
            css`
                :host {
                    display: flex;
                    flex-direction: column;

                    --iron-overlay-backdrop-opacity: 0.54;
                }
            `,
        ];
    }

    render() {
        return html`
            <tcg-signin-aware
                id="signinAware"
                scope="openid name api api_tcg_claims idString email"
                callback-uri="/"
                post-logout-redirect-uri="/"
            ></tcg-signin-aware>

            <iron-location
                @path-changed=${async (e) => {
                    await this._router.goto(e.detail.value);
                }}
            ></iron-location>

            <store-provider .value="${this.store}">
                ${this._renderMainBody()}

                <tcg-costregistration-dialog></tcg-costregistration-dialog>
                <tcg-learningplan-dialog></tcg-learningplan-dialog>
                <tcg-scorm-player-dialog></tcg-scorm-player-dialog>
            </store-provider>

            ${this._user && this._settings.portalIdentifier
                ? html` <sdb-embedded-elearning-dialog
                          id="app_elearning_dialog"
                          .settings=${{
                              portalIdentifier: this._settings.portalIdentifier,
                              env: this._settings.compositionEnvironment,
                          }}
                          .user=${this._user}
                      ></sdb-embedded-elearning-dialog>
                      <sdb-embedded-preview-dialog
                          id="app_preview_dialog"
                          .settings=${{
                              portalIdentifier: this._settings.portalIdentifier,
                              env: this._settings.compositionEnvironment,
                          }}
                          .user=${this._user}
                      ></sdb-embedded-preview-dialog>`
                : nothing}
        `;
    }

    get signedIn() {
        return this._signedIn;
    }

    @property({type: Object})
    accessor menu = null;

    @property({type: Boolean})
    set signedIn(value) {
        this._signedIn = value;

        this.dispatchEvent(
            new CustomEvent('signed-in-changed', {
                detail: {signedIn: value},
                bubbles: true,
                composed: true,
            }),
        );
    }

    @property({type: Boolean, reflect: true})
    accessor sdb = false;

    @property({type: Boolean, reflect: true})
    accessor tcg = false;

    @state()
    accessor _settings;

    @state()
    accessor _user;

    @state()
    accessor _styleVars;

    @query('#app_elearning_dialog')
    accessor elearningDialog;

    static get properties() {
        return {
            _loading: {
                type: Boolean,
                observer: '_loadingChanged',
            },
            _timeout: {
                type: Number,
            },
        };
    }

    connectedCallback() {
        super.connectedCallback();

        store.dispatch({type: 'MINERVASETTINGS_REQUESTED'});

        this.useKeyIfMissing = true;
    }

    async fetchStyles(apiUrl) {
        const response = await fetch(`${apiUrl}/style`);

        this._styleVars = await response.json();
        this.applyStyles();
    }

    applyStyles() {
        // Transform the CSS text to replace :host selector with :root
        // This allows CSS custom properties to work at document level
        const transformedCssText = colors.cssText.replace(/:host\s*{/g, ':root {');

        // Create CSS text from _styleVars
        let customVarsCss = ':root {\n';

        for (const [key, value] of Object.entries(this._styleVars)) {
            // Only add variables. _styleVars also contains other properties that can't work.
            if (key.startsWith('--')) {
                customVarsCss += `  ${key}: ${value};\n`;
            }
        }
        customVarsCss += '}\n';

        // Combine both CSS texts, with custom vars coming last for proper overrides
        const combinedCss = transformedCssText + '\n' + customVarsCss;

        const styleSheet = new CSSStyleSheet();

        styleSheet.replaceSync(combinedCss);
        document.adoptedStyleSheets = [...document.adoptedStyleSheets, styleSheet];
    }

    stateChanged(state) {
        super.stateChanged(state);

        // Update userStore
        if (state.oidc.user) {
            userStore.updateUser(state.oidc.user);
        } else {
            userStore.reset();
        }

        // Update settingsStore
        if (state.minervaSettings) {
            settingsStore.updateSettings(state.minervaSettings);
        }

        if (state.minervaSettings.apiUrl) {
            gravatarStore.setGravatarUrl(state.minervaSettings.apiUrl);
        }

        if (state.minervaSettings.redirects) {
            const current = window.location.hostname.toLowerCase();

            for (const hostFrom of Object.keys(state.minervaSettings.redirects)) {
                if (hostFrom.toLowerCase() === current) {
                    const hostTo = state.minervaSettings.redirects[hostFrom];

                    if (hostTo) {
                        window.location.href = `${window.location.protocol}//${hostTo}${window.location.pathname}${window.location.search}`;
                    }
                }
            }
        }

        // force sdb class, remainder of old/new UI. This sets an class="sdb" on this element, that's used in sharedStyles
        this.sdb = true;

        this._unAuthorized = state.minervaSettings.forceLogout === true;
        this._notFound = state.minervaSettings.notFound === true;

        if (this._unAuthorized) {
            this._loadModule(
                './aca-unauthorized-page.js',
                async () => await import('@/app/aca-unauthorized-page.js'),
            );
            this._hideSplash();
        }

        if (this._notFound) {
            this._loadModule(
                './aca-portal-does-not-exists-page.js',
                async () => await import('@/app/aca-portal-does-not-exists-page.js'),
            );
            this._hideSplash();
        }

        if (typeof state.minervaSettings.portalIdentifier !== 'undefined') {
            // check if we're done loading
            this._hideSplash();
        }

        this._settings = state.minervaSettings;

        if (!this._stylesApiUrl && this._settings?.apiUrl) {
            this._stylesApiUrl = this._settings.apiUrl;
            this.fetchStyles(this._stylesApiUrl);
        }

        if (state.minervaSettings.customerTitle) {
            document.title = state.minervaSettings.customerTitle;
        }

        this._hasHeaders = !!state.minervaSettings?.headers?.Authorization;

        if (
            state.minervaSettings.loading === false &&
            state.oidc.user &&
            this.__prevAccessToken !== state.oidc.user.access_token
        ) {
            this.__prevAccessToken = state.oidc.user.access_token;
            store.dispatch({type: 'MINERVASETTINGS_REQUESTED'});
        }

        if (state.oidc.user && state.oidc.user.access_token) {
            const signInAware = this.shadowRoot.querySelector('#signinAware');

            if (signInAware && signInAware.token?.access_token !== state.oidc.user.access_token) {
                signInAware.saveToken(state.oidc.user);
            }
        }

        if (!state.oidc.user) {
            this._silentSignIn = false;
            this._signIn = true;
            this.signedIn = false;
        } else {
            this._silentSignIn = false;
            this._signIn = false;
            this.signedIn = true;
        }

        if (state.signIn.pending === false && state.signIn.signedIn === true) {
            this._silentSignIn = false;
            this._signIn = false;
            this.signedIn = true;
        }

        this._user = state.oidc.user;

        ///
        /// Sign in to HL if we have the necessary settings and an auth header that we haven't used before
        ///

        if (
            this._settings?.hlUrl && // hlUrl is only set when HL integration is enabled, so this check ensures HL integration is enabled
            this._settings?.portalIdentifier &&
            this._settings?.headers?.Authorization?.trim() &&
            // Trigger sign-in when we have a new auth header
            // this forces a new sign-in when the user or access_token changes
            // but prevents repeated sign-in attempts with the same token
            (!this.__prevAuthHeader ||
                this.__prevAuthHeader !== this._settings?.headers?.Authorization?.trim())
        ) {
            this._signInToHl();
        }
        ///
        /// Sign in to HL
        ///
    }

    @debounce(300)
    async _signInToHl() {
        await signInToHl(this._settings);
    }

    _renderMainBody() {
        // TODO: Convert to route
        if (this._notFound) {
            return html`<aca-portal-does-not-exists-page></aca-portal-does-not-exists-page>`;
        }

        // TODO: Convert to route
        if (this._unAuthorized) {
            return html`<aca-unauthorized-page></aca-unauthorized-page>`;
        }

        // TODO: Convert to route
        if (this._signIn) {
            return html`<aca-signin-page></aca-signin-page>`;
        }

        // TODO: Convert to route
        // if(this._silentSignIn) {
        //     return html`<aca-silent-signin-page></aca-silent-signin-page>`;
        // }

        // TODO: route to _notFound || _unAuthorized || _signIn || _silentSignIn from aca-main-page
        return html`${this._router.outlet()}`;
    }

    _renderAcaMainPage() {
        if (!(this.signedIn && this._hasHeaders)) return;

        const route = window.location.pathname;

        if (this._isRouteAllowed(this.menu, route)) {
            return html`<aca-main-page></aca-main-page>`;
        }

        // Het menu is nog onderweg: dit is wachten, geen weigering (#6566).
        if (isAwaitingMenu(this.menu, route)) return nothing;

        return html`<tcg-noresults
            localize-title="forbidden"
            localize-text="youAreNotAuthorizedToViewThisInformation"
        ></tcg-noresults>`;
    }

    promptForUpdate(updateAccepted) {
        this.shadowRoot.querySelector('#appUpdate').open();
        this._appUpdateResolver = updateAccepted;
    }

    _loadingChanged() {
        if (this._loading === undefined) return;
        if (this._loading) {
            window.clearTimeout(this._timeout);
            window.requestAnimationFrame(() => {
                document.querySelector('#splash').classList.remove('hide');
            });
        } else {
            this._hideSplash();
        }
    }

    _performHide() {
        window.requestAnimationFrame(() => {
            this._splash.classList.add('hide');

            window.setTimeout(() => {
                this._splash.parentNode?.removeChild(this._splash);
            }, 0);
        });
    }

    _hideSplash() {
        this._splash = document.querySelector('#splash');
        if (!this._splash) return;

        if (this._splash.classList.contains('hide')) return;

        window.clearTimeout(this._timeout);
        // const timeout = this._selected !== 0 ? 0 : 2000;
        const timeout = 50;

        if (!this._loading) {
            this._performHide();
        } else {
            this._timeout = window.setTimeout(() => {
                if (this._loading) {
                    return;
                } else {
                    this._performHide();
                }
            }, timeout);
        }
    }
}
